"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "./lib/utils"
import { useFlatUI } from './flat-ui';
import type { Colors as ColorsType } from './types';
import { useRipple } from './effects/ripple';
const Tabs = TabsPrimitive.Root;

type TabColor = ColorsType | 'default';

const TabsVariantContext = React.createContext<'outlined' | 'contained'>(
  'contained'
);
const TabsColorContext = React.createContext<TabColor>('default');

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: 'outlined' | 'contained';
    color?: TabColor;
  }
>(({ className, variant = 'contained', color = 'default', ...props }, ref) => (
  <TabsColorContext.Provider value={color}>
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          'inline-flex h-10 items-center justify-center',
          variant === 'contained' && 'rounded-md bg-muted p-1',
          variant === 'outlined' && 'gap-4',
          'text-muted-foreground',
          className
        )}
        {...props}
      />
    </TabsVariantContext.Provider>
  </TabsColorContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    variant?: 'outlined' | 'contained';
    color?: TabColor;
  }
>(({ className, variant: propVariant, color: propColor, ...props }, ref) => {
  const { rippleHandlers, rippleClassName } = useRipple({
    variant: 'ghost',
  });
  const contextVariant = React.useContext(TabsVariantContext);
  const contextColor = React.useContext(TabsColorContext);
  const variant = propVariant ?? contextVariant;
  const color = propColor ?? contextColor;
  const flat = useFlatUI();


  const TEXT_COLOR_CLASSES = [
    'data-[state=active]:data-[color=primary]:text-primary',
    'data-[state=active]:data-[color=secondary]:text-secondary',
    'data-[state=active]:data-[color=accent]:text-accent',
    'data-[state=active]:data-[color=info]:text-info',
    'data-[state=active]:data-[color=success]:text-success',
    'data-[state=active]:data-[color=warning]:text-warning',
    'data-[state=active]:data-[color=error]:text-error',
  ];

  const BORDER_COLOR_CLASSES = [
    'data-[state=active]:data-[color=primary]:border-primary',
    'data-[state=active]:data-[color=secondary]:border-secondary',
    'data-[state=active]:data-[color=accent]:border-accent',
    'data-[state=active]:data-[color=info]:border-info',
    'data-[state=active]:data-[color=success]:border-success',
    'data-[state=active]:data-[color=warning]:border-warning',
    'data-[state=active]:data-[color=error]:border-error',
  ];

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-color={color}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'contained' && [
          'rounded-sm data-[state=active]:shadow-sm',
          flat && 'data-[state=active]:shadow-none',
          'data-[state=active]:bg-background',
          color === 'default'
            ? 'data-[state=active]:text-foreground'
            : TEXT_COLOR_CLASSES,
        ],
        variant === 'outlined' && [
          'border-b-2 border-transparent',
          color === 'default'
            ? [
                'data-[state=active]:border-primary',
                'data-[state=active]:text-foreground',
              ]
            : [...BORDER_COLOR_CLASSES, ...TEXT_COLOR_CLASSES],
        ],
        rippleClassName,
        className
      )}
      {...props}
      {...rippleHandlers}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
